import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./Subscription.module.css";
import Button from "../../component/button/Button";
import InputText from "../../component/inputText/InputText";
import Pagination from "../pagination/Pagination";
import { showToast } from "../../utils/Toastify/Toastify";
import { FiRefreshCw, FiTrash2 } from "react-icons/fi";
import { debounce } from "../../utils/debounce";
import {
  getSubscriptionPlans,
  deleteSubscriptionPlan,
  updateSubscriptionPlanStatus,
  getPromos,
} from "../../api/HttpApis";
import PlanModal from "./PlanModal";
import ConfirmDialog from "../Submissions/ConfirmDialog";

const adBadge = (adConfig) => {
  if (!adConfig) return null;
  const { pre_roll, mid_roll, post_roll } = adConfig;
  if (!pre_roll && !mid_roll && !post_roll) return "Ad-free";
  const parts = [];
  if (pre_roll) parts.push("Pre");
  if (mid_roll) parts.push("Mid");
  if (post_roll) parts.push("Post");
  return parts.join(" + ");
};

const contentAccessLabel = (val) =>
  ({ limited: "Limited access", most: "Most content", full: "Full access" }[val] || val);

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [activePromos, setActivePromos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
        setCurrentPage(1);
      }, 400),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSetSearch(value);
  };

  const [view, setView] = useState("cards");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // { type: "add" } | { type: "edit", plan } — set when Add/Edit is clicked,
  // so we can confirm with the user before opening the plan form.
  const [confirmAction, setConfirmAction] = useState(null);

  // Map of plan_id -> matching active promo, for showing pills on cards.
  // Promos are plan-level (one-time 90-day pass), so the pill shows on ALL
  // prices of a plan if a promo applies to that plan.
  const promoByPlanId = (() => {
    const map = {};
    for (const promo of activePromos) {
      if (!promo.is_currently_active) continue;
      for (const pid of promo.applies_to_plan_ids || []) {
        // First active promo wins (matches backend's "earliest valid_until" tie-break).
        if (!map[pid]) map[pid] = promo;
      }
    }
    return map;
  })();

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch plans + active promos in parallel; promos are best-effort
      // (don't block the page if the promo endpoint hiccups).
      const [plansResponse, promosResponse] = await Promise.all([
        getSubscriptionPlans({
          page: currentPage,
          limit: rowsPerPage,
          search,
        }),
        getPromos({ page: 1, limit: 100, status: 1 }).catch(() => null),
      ]);

      const list =
        plansResponse?.subscription_plans ||
        plansResponse?.data?.subscription_plans ||
        [];
      const total =
        plansResponse?.pagination?.total ??
        plansResponse?.data?.pagination?.total ??
        list.length;
      setPlans(list);
      setTotalCount(total);

      const promos =
        promosResponse?.promos || promosResponse?.data?.promos || [];
      setActivePromos(promos);
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to load plans",
        "error"
      );
      setPlans([]);
      setTotalCount(0);
      setActivePromos([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, search]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleAdd = () => {
    setConfirmAction({ type: "add" });
  };

  const handleEdit = (plan) => {
    setConfirmAction({ type: "edit", plan });
  };

  const handleConfirmProceed = () => {
    if (!confirmAction) return;
    setEditData(confirmAction.type === "edit" ? confirmAction.plan : null);
    setIsModalOpen(true);
    setConfirmAction(null);
  };

  const handleConfirmCancel = () => {
    setConfirmAction(null);
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"? This cannot be undone.`))
      return;
    setDeletingId(plan.id);
    try {
      const response = await deleteSubscriptionPlan(plan.id);
      showToast(response?.message || "Plan deleted", "success");
      fetchPlans();
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to delete plan",
        "error"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleStatus = async (plan) => {
    const next = plan.status === "Active" ? 0 : 1;
    try {
      const response = await updateSubscriptionPlanStatus(plan.id, next);
      showToast(response?.message || "Status updated", "success");
      fetchPlans();
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to update status",
        "error"
      );
    }
  };

  const handleModalClose = (didChange) => {
    setIsModalOpen(false);
    setEditData(null);
    if (didChange) fetchPlans();
  };

  const formatCycle = (p) => {
    const interval = p.billing_interval > 1 ? `${p.billing_interval} ` : "";
    const suffix = p.billing_interval > 1 ? "s" : "";
    return `/ ${interval}${p.billing_cycle}${suffix}`;
  };

  const renderCards = () => (
    <div className={styles["plans-grid"]}>
      {plans.length === 0 ? (
        <div className={styles["plan-card-empty"]}>
          No plans found. Click "+ Add Plan" to create one.
        </div>
      ) : (
        plans.map((plan) => {
          const features = Array.isArray(plan.features) ? plan.features : [];
          const prices = Array.isArray(plan.prices) ? plan.prices : [];
          const adText = adBadge(plan.ad_config);
          return (
            <div key={plan.id} className={styles["plan-card"]}>
              <div className={styles["plan-card-header"]}>
                {/* min-width: 0 + flex: 1 — this nameless flex child holds the
                    long text (name/tagline/description). Without min-width:0 a
                    pasted unbroken string refuses to shrink and pushes the
                    card wider than its grid column. */}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h3 className={styles["plan-card-name"]}>{plan.name}</h3>
                  {plan.tagline && (
                    <p className={styles["plan-card-tagline"]}>
                      {plan.tagline}
                    </p>
                  )}
                  {plan.description && (
                    <p className={styles["plan-card-desc"]}>
                      {plan.description}
                    </p>
                  )}
                </div>
                <span
                  className={`${styles.status} ${
                    plan.status === "Active" ? styles.active : styles.expired
                  }`}
                  onClick={() => handleToggleStatus(plan)}
                  style={{ cursor: "pointer" }}
                  title="Click to toggle status"
                >
                  {plan.status}
                </span>
              </div>

              <div className={styles["plan-card-prices"]}>
                {prices.length === 0 ? (
                  <span className={styles["plan-card-empty-features"]}>
                    No prices set
                  </span>
                ) : (
                  prices.map((p) => {
                    const promo = promoByPlanId[plan.id];
                    const hasPills = !!promo || p.trial_days > 0 || p.is_default;
                    return (
                      <div
                        key={p.id || `${p.billing_cycle}-${p.billing_interval}`}
                        className={`${styles["plan-card-price-row"]} ${
                          p.is_default ? styles["plan-card-price-row-default"] : ""
                        }`}
                      >
                        <div className={styles["plan-card-price-line"]}>
                          <span className={styles["plan-card-price-amount-line"]}>
                            {Number(p.price) === 0
                              ? "FREE"
                              : `${p.currency || "USD"} ${Number(p.price).toFixed(2)}`}
                          </span>
                          <span className={styles["plan-card-price-cycle-line"]}>
                            {formatCycle(p)}
                          </span>
                        </div>
                        {hasPills && (
                          <div className={styles["plan-card-price-pills"]}>
                            {promo && (
                              <span
                                className={styles["plan-card-price-promo-pill"]}
                                title={`${promo.name} — ${promo.currency} ${Number(promo.intro_price).toFixed(2)} for first ${promo.duration_days} days`}
                              >
                                🔥 {promo.currency} {Number(promo.intro_price).toFixed(2)} for {promo.duration_days}d
                              </span>
                            )}
                            {p.trial_days > 0 && (
                              <span className={styles["plan-card-price-trial-pill"]}>
                                {p.trial_days}-day trial
                              </span>
                            )}
                            {p.is_default && (
                              <span className={styles["plan-card-price-default-pill"]}>
                                Default
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {adText && (
                <span className={styles["plan-card-ad-badge"]}>{adText}</span>
              )}

              <div className={styles["plan-card-specs"]}>
                <span className={styles["plan-card-spec"]}>
                  {contentAccessLabel(plan.content_access)}
                </span>
                <span className={styles["plan-card-spec"]}>
                  {plan.video_quality}
                </span>
                <span className={styles["plan-card-spec"]}>
                  {plan.max_devices} device
                  {plan.max_devices > 1 ? "s" : ""}
                </span>
                <span className={styles["plan-card-spec"]}>
                  {plan.max_profiles} profile
                  {plan.max_profiles > 1 ? "s" : ""}
                </span>
              </div>

              {features.length > 0 ? (
                <ul className={styles["plan-card-features"]}>
                  {features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              ) : (
                <div className={styles["plan-card-empty-features"]}>
                  No features listed
                </div>
              )}

              <div className={styles["plan-card-footer"]}>

                
                <span style={{ fontSize: "0.8rem", opacity: 0.7 }}>
                  {plan.subscribers_count ?? 0} subscriber
                  {(plan.subscribers_count ?? 0) === 1 ? "" : "s"}
                </span>
                <div className={styles["plan-card-actions"]}>
                  <Button variant="outline" onClick={() => handleEdit(plan)}>
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleDelete(plan)}
                    loading={deletingId === plan.id}
                    disabled={
                      deletingId === plan.id || (plan.subscribers_count ?? 0) > 0
                    }
                    title={
                      (plan.subscribers_count ?? 0) > 0
                        ? "Cannot remove: this plan has subscribers"
                        : undefined
                    }
                  >
                    {deletingId === plan.id ? "Removing..." : "Remove"}
                  </Button>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  const renderTable = () => (
    <table className={styles["subscription-table"]}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Prices</th>
          <th>Access</th>
          <th>Quality</th>
          <th>Devices</th>
          <th>Ads</th>
          <th>Subscribers</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {plans.length === 0 ? (
          <tr>
            <td colSpan="9" style={{ textAlign: "center" }}>
              No plans found.
            </td>
          </tr>
        ) : (
          plans.map((plan) => {
            const prices = Array.isArray(plan.prices) ? plan.prices : [];
            const adText = adBadge(plan.ad_config) || "—";
            return (
              <tr key={plan.id}>
                <td>
                  <div style={{ fontWeight: 600 }}>{plan.name}</div>
                  {plan.tagline && (
                    <div style={{ fontSize: "0.75rem", opacity: 0.7, fontStyle: "italic" }}>
                      {plan.tagline}
                    </div>
                  )}
                </td>
                <td>
                  {prices.length === 0 ? (
                    "—"
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {prices.map((p) => (
                        <span key={p.id || `${p.billing_cycle}-${p.billing_interval}`}>
                          {Number(p.price) === 0
                            ? "FREE"
                            : `${p.currency} ${Number(p.price).toFixed(2)}`}
                          {" "}
                          <small style={{ opacity: 0.6 }}>{formatCycle(p)}</small>
                          {p.trial_days > 0 && (
                            <small style={{ marginLeft: 4, opacity: 0.7 }}>
                              + {p.trial_days}-day trial
                            </small>
                          )}
                          {p.is_default && (
                            <small style={{ marginLeft: 4, opacity: 0.7 }}>
                              (default)
                            </small>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td>{contentAccessLabel(plan.content_access)}</td>
                <td>{plan.video_quality}</td>
                <td>{plan.max_devices}</td>
                <td>{adText}</td>
                <td>{plan.subscribers_count ?? 0}</td>
                <td>
                  <span
                    className={`${styles.status} ${
                      plan.status === "Active" ? styles.active : styles.expired
                    }`}
                    onClick={() => handleToggleStatus(plan)}
                    style={{ cursor: "pointer" }}
                    title="Click to toggle status"
                  >
                    {plan.status}
                  </span>
                </td>
                <td>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <Button variant="outline" onClick={() => handleEdit(plan)}>
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDelete(plan)}
                      loading={deletingId === plan.id}
                      disabled={
                        deletingId === plan.id || (plan.subscribers_count ?? 0) > 0
                      }
                      title={
                        (plan.subscribers_count ?? 0) > 0
                          ? "Cannot remove: this plan has subscribers"
                          : undefined
                      }
                    >
                      {deletingId === plan.id ? "Removing..." : "Remove"}
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );

  return (
    <div className={styles["tab-content"]}>
      <div className={styles["plans-toolbar"]}>
        <div>
          <h2 className={styles["table-title"]}>Subscription Plans</h2>
          <p className={styles["table-subtitle"]}>
            Create and manage pricing tiers
          </p>
        </div>

        <div className={styles["plans-toolbar-right"]}>
          <div className={styles["view-toggle"]}>
            <button
              type="button"
              className={view === "cards" ? styles.active : ""}
              onClick={() => setView("cards")}
            >
              Cards
            </button>
            <button
              type="button"
              className={view === "table" ? styles.active : ""}
              onClick={() => setView("table")}
            >
              Table
            </button>
          </div>

          <div style={{ width: "240px" }}>
            <InputText
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Search plans..."
              className={styles["input-dark"]}
            />
          </div>

          <Button onClick={handleAdd}>+ Add Plan</Button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "2rem" }}>Loading...</div>
      ) : view === "cards" ? (
        renderCards()
      ) : (
        renderTable()
      )}

      {totalCount > rowsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          setRowsPerPage={setRowsPerPage}
          totalCount={totalCount}
          dataLength={plans.length}
          columnsLength={9}
          className={styles["history-pagination"]}
        />
      )}

      {isModalOpen && (
        <PlanModal
          editData={editData}
          onClose={handleModalClose}
          totalPlans={plans.length}
        />
      )}

      <ConfirmDialog
        open={!!confirmAction}
        title={
          confirmAction?.type === "edit"
            ? "Update Subscription Plan"
            : "Add Subscription Plan"
        }
        message={
          confirmAction?.type === "edit"
            ? "Changing this plan will also affect users on the frontend who are currently using this plan. Are you sure you want to continue?"
            : "This new plan will be available to users on the frontend immediately after creation. Are you sure you want to continue?"
        }
        confirmLabel="Yes"
        tone="danger"
        onCancel={handleConfirmCancel}
        onConfirm={handleConfirmProceed}
        titleClassName={styles["plan-confirm-title"]}
        confirmClassName={styles["plan-confirm-yes"]}
        cancelClassName={styles["plan-confirm-cancel"]}
      />
    </div>
  );
};

export default Plans;